// Grid code table renderer: loads the table rows and renders them with a sticky header row (the first row when it looks like a header), caption, size and source clause.
import { useQuery } from "@tanstack/react-query";

import { catalogGetTableOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { ProblemState } from "@/components/common/ProblemState";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";

const MAX_HEADER_CHARS = 80;

export function firstRowIsHeader(rows: readonly (readonly string[])[]): boolean {
  const [first, ...rest] = rows;
  if (first === undefined || rest.length === 0 || first.length === 0) {
    return false;
  }
  return first.every(
    (cell) => cell.trim().length > 0 && cell.length <= MAX_HEADER_CHARS && !cell.includes("\n"),
  );
}

interface TableGridProps {
  rows: readonly (readonly string[])[];
  caption: string;
  className?: string | undefined;
}

export function TableGrid({ rows, caption, className }: TableGridProps) {
  const header = firstRowIsHeader(rows);
  const [head, ...body] = header ? rows : [undefined, ...rows];
  const columns = rows.reduce((widest, row) => Math.max(widest, row.length), 0);
  return (
    <div className={cn("pane-scroll rounded-md border", className)}>
      <table className="w-full border-collapse text-ui">
        <caption className="sr-only">{caption}</caption>
        {head ? (
          <thead>
            <tr>
              {Array.from({ length: columns }, (_, index) => (
                <th
                  key={index}
                  scope="col"
                  className="sticky top-0 z-(--z-sticky) border-b bg-subtle px-3 py-2 text-left align-bottom text-xs font-semibold text-ink-2"
                >
                  {head[index] ?? ""}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-border-subtle last:border-b-0">
              {Array.from({ length: columns }, (_, index) => (
                <td key={index} className="px-3 py-2 align-top whitespace-pre-line text-ink-2">
                  {row?.[index] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TableViewProps {
  docUid: string;
  tableKey: string;
  caption: string;
  className?: string;
}

export function TableView({ docUid, tableKey, caption, className }: TableViewProps) {
  const table = useQuery(catalogGetTableOptions({ path: { doc_uid: docUid, table_key: tableKey } }));
  if (table.isError) {
    return (
      <ProblemState
        problem={table.error}
        compact
        retry={() => {
          void table.refetch();
        }}
      />
    );
  }
  if (table.isPending) {
    return (
      <div aria-hidden className="flex flex-col gap-1.5">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-7 w-full" />
        ))}
      </div>
    );
  }
  if (table.data.rows.length === 0) {
    return <p className="m-0 text-ui text-muted-foreground">This table has no rows.</p>;
  }
  return <TableGrid rows={table.data.rows} caption={caption} className={className} />;
}
